import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const useBottomDrawerRefs = () => {
  // Non-reactive refs
  const overlayRef = useRef<HTMLElement | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);
  const footerRef = useRef<HTMLElement | null>(null);
  const contentRef = useRef<HTMLElement | null>(null);

  // Refs as state to force re-renders
  const [elements, setElements] = useState<{
    root: HTMLElement | null;
    overlay: HTMLElement | null;
    header: HTMLElement | null;
    footer: HTMLElement | null;
    content: HTMLElement | null;
  }>({ content: null, footer: null, header: null, overlay: null, root: null });

  const hideFromTalkback = useCallback((element: HTMLElement) => {
    element.role = "presentation";
    element.ariaModal = "false";
    element.ariaLabel = null;
    // This allows not focusing the bottom drawer on Android
    element.removeAttribute("tabindex");
    element.onfocus = () => {
      element.blur();
    };
  }, []);

  const queryAndPrepare = useCallback(
    (query: string) => {
      const el = document.querySelector(query) as HTMLElement;
      if (el) hideFromTalkback(el);
      return el;
    },
    [hideFromTalkback],
  );

  // Setup inital root element
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const element = document.querySelector(
        '[data-rsbs-root="true"]',
      ) as HTMLElement;
      if (element) {
        setElements((prev) => ({ ...prev, root: element }));
        // When root element is found, disconnect observer
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Listen for changes on bottom drawer root element
  useEffect(() => {
    if (!elements.root) return;

    const update = (
      selector: string,
      ref: RefObject<HTMLElement> | null,
      key: keyof typeof elements,
    ) => {
      const element = queryAndPrepare(selector);
      if (element) {
        ref.current = element;

        setElements((prev) => {
          if (prev[key] === element) return prev;
          return { ...prev, [key]: element };
        });
      }
    };

    const observer = new MutationObserver(() => {
      update('[data-rsbs-overlay="true"]', overlayRef, "overlay");
      update('[data-rsbs-header="true"]', headerRef, "header");
      queryAndPrepare('[data-rsbs-content="true"]');
      update('[data-rsbs-scroll="true"]', contentRef, "content");
      update('[data-rsbs-footer="true"]', footerRef, "footer");
    });

    observer.observe(elements.root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [queryAndPrepare, elements.root]);

  return {
    contentRef,
    elements,
    footerRef,
    headerRef,
    overlayRef,
  };
};

export default useBottomDrawerRefs;
